import * as Sentry from "@sentry/browser";

const STORAGE_KEY = "againSignupContext";

export interface SignupContext {
  /** Full URL of the first page of this session, query string included. */
  url?: string | null;
  /** document.referrer at that first page — the external source, if any. */
  referrer?: string | null;
  /** Full URL at the moment the signup mutation is fired. */
  currentUrl?: string | null;
}

type StoredLanding = {
  url: string;
  referrer: string | null;
};

const readStorage = (): Storage | null => {
  try {
    // Private-mode Safari and cookie-blocking extensions can make this throw or
    // return null; attribution is never worth breaking a signup over.
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch (err: any) {
    return null;
  }
};

/**
 * Records where this browser session started, once.
 *
 * This has to run at the very first client entry, before anything else:
 * `/get/:slug` redirects to `/{market}/get/:slug` and drops the query string on
 * the way, and the BankID app-switch rebuilds the page URL from
 * protocol+host+pathname. By the time the user presses "create account" the
 * campaign parameters and the external referrer are both long gone.
 *
 * First write wins — later navigations (including the full page reloads our own
 * redirects trigger) must not overwrite the original entry point.
 */
export const captureLandingContext = () => {
  try {
    const storage = readStorage();
    if (!storage || storage.getItem(STORAGE_KEY)) {
      return;
    }
    const landing: StoredLanding = {
      url: window.location.href,
      referrer: document.referrer || null,
    };
    storage.setItem(STORAGE_KEY, JSON.stringify(landing));
  } catch (err: any) {
    Sentry.captureException(err);
  }
};

const getLandingContext = (): StoredLanding | null => {
  try {
    const raw = readStorage()?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.url === "string" ? parsed : null;
  } catch (err: any) {
    Sentry.captureException(err);
    return null;
  }
};

/**
 * Builds the `signupContext` variable for the account-creation mutations.
 * Falls back to the current page when nothing was captured — better a partial
 * attribution than none.
 */
export const getSignupContext = (): SignupContext => {
  const landing = getLandingContext();
  const currentUrl =
    typeof window === "undefined" ? null : window.location.href;

  return {
    url: landing?.url ?? currentUrl,
    referrer: landing?.referrer ?? null,
    currentUrl,
  };
};
